import React, { useState, useEffect } from 'react';
import { Bookmark, BookmarkPlus } from 'lucide-react';
import { bookmarkService } from '../lib/bookmarkService';

interface BookmarkButtonProps {
  docketNumber: string;
  title?: string;
  className?: string;
  /** Dark nav / header surfaces */
  tone?: 'default' | 'dark';
  onToggle?: (isBookmarked: boolean) => void;
}

export const BookmarkButton: React.FC<BookmarkButtonProps> = ({ 
  docketNumber, 
  title, 
  className = '',
  tone = 'default',
  onToggle 
}) => {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkBookmarkStatus = async () => {
      try {
        const status = await bookmarkService.isBookmarked(docketNumber);
        setIsBookmarked(status);
      } catch (error) {
        console.error('Error checking bookmark status:', error);
      }
    };

    checkBookmarkStatus();
  }, [docketNumber]);

  const handleToggle = async () => {
    try {
      setLoading(true);
      const newStatus = await bookmarkService.toggleBookmark(docketNumber, title);
      setIsBookmarked(newStatus);
      onToggle?.(newStatus);
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    } finally {
      setLoading(false);
    }
  };

  const surfaceClass =
    tone === 'dark'
      ? isBookmarked
        ? 'bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/35 text-amber-200 hover:text-amber-100'
        : 'bg-white/[0.08] hover:bg-white/[0.13] border border-white/15 text-white/80 hover:text-white'
      : isBookmarked
        ? 'bg-yellow-100 hover:bg-yellow-200 border border-yellow-300 text-yellow-700 hover:text-yellow-800'
        : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:text-gray-700';

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${surfaceClass} ${className}`}
      title={isBookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : isBookmarked ? (
        <Bookmark className="w-4 h-4 fill-current" />
      ) : (
        <BookmarkPlus className="w-4 h-4" />
      )}
      <span className="hidden sm:inline">
        {isBookmarked ? 'Bookmarked' : 'Bookmark'}
      </span>
    </button>
  );
};
